import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  ElementRef,
  inject,
  input,
  output,
  signal,
  viewChild,
} from "@angular/core";
import {
  BubbleController,
  CategoryScale,
  Chart,
  Legend,
  LinearScale,
  PointElement,
  Tooltip,
} from "chart.js";
import { ArticlesInfosFacadeService } from "../../services/article-infos/article-infos-facade.service";
import { CommonModule } from "@angular/common";
import { BilanPiedDeChaine } from "../../../pied-de-chaine/models/bilan-pied-de-chaine.model";
import { OF } from "../../../of/models/of.model";
import { SharedModule } from "../../../../shared/shared.module";
import { TypePercent } from "../../../../shared/enums/enums/typePercent.enum";
import { ArticleInfosHelperService } from "../../services/article-infos/article-infos-helper.service";
import { EntityMetadata } from "../../../../shared/store/store-types.model";
import { Widget } from "../../../dashboard/models/dashboard.model";
import { ArticleFacadeService } from "../../services/article/article-facade.service";
import { PopoverModule } from "../../../../shared/modules/popover/popover.module";
import { ChipsToggleComponent } from "../../../../shared/modules/chips-toggle/chips-toggle.component";
import { OfCardComponent } from "../../../of/components/of-card/of-card.component";
import { PercentComponent } from "../../../../shared/components/percent/percent.component";
import { WidgetTitleComponent } from "../../../../shared/components/widget-title/widget-title.component";
import { ChipsComponent } from "../../../../shared/modules/chips/chips.component";
import { AutoSignificantDigitsPipe } from "../../../../shared/pipes/number-sig.pipe";
import { parseNumber } from "../../../../shared/utils/number-utils.service";

export interface Grouped {
  articleId: string;
  bilans: { bilan: BilanPiedDeChaine }[];
}

/** Agrégations par ref (axes actuels) */
type RefRow = {
  ref: string;
  totalConso: number; // Σ consoProd
  lossPct: number; // (1 - Σ theo / Σ conso) × 100
  lossAbsKg: number; // Σ max(0, conso - theo)
  unitCostWAvg: number; // (Σ cu*conso) / (Σ conso)
  lossEur: number; // lossAbsKg * unitCostWAvg
  n: number;
};

/** Lignes OF détaillées (panneau résultat) */
type OfRow = {
  of: OF;
  consoProd: number;
  consoTheorique: number;
  pertesAbsKg: number; // max(0, conso - theo)
  pertesPct: number; // ((conso - theo)/theo)*100 (0 si theo=0)
  pertesEur: number; // pertesAbsKg * coutUnitaire
};

export interface BubbleClickPayload {
  refId: string;
  label: string;
  avgConso: number; // = totalConso
  avgLossPct: number; // = lossPct
  lossEur: number;
}

type ViewType = "GRAPH" | "LIST";
type SortBy = "LOSS_EUR" | "LOSS_PCT" | "CONSO" | "ALPHA";

Chart.register(
  LinearScale,
  CategoryScale,
  PointElement,
  Tooltip,
  Legend,
  BubbleController,
);

@Component({
  selector: "vl-quick-win-scatter",
  standalone: true,
  imports: [
    CommonModule,
    SharedModule,
    PopoverModule,
    ChipsToggleComponent,
    OfCardComponent,
    PercentComponent,
    WidgetTitleComponent,
    ChipsComponent,
    AutoSignificantDigitsPipe,
  ],
  templateUrl: "./quick-win-scatter.component.html",
  styleUrls: ["./quick-win-scatter.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class QuickWinScatterComponent {
  articleFacade = inject(ArticleFacadeService);
  stats = this.articleFacade.getState().stats;

  metadata = input<EntityMetadata>();
  widget = input<Widget>();
  articleInfosFacade = inject(ArticlesInfosFacadeService);

  groups = computed(() => {
    if (this.stats()?.bilans?.length) {
      const map = new Map<
        string,
        { articleId: string; bilans: { bilan: BilanPiedDeChaine }[] }
      >();
      for (const bilan of this.stats()?.bilans ?? []) {
        const articleId = bilan?.of?.articleId ?? "__unknown__";
        let bucket = map.get(articleId);
        if (!bucket) {
          bucket = { articleId, bilans: [] };
          map.set(articleId, bucket);
        }
        bucket.bilans.push({ bilan });
      }
      return Array.from(map.values());
    }
    return [];
  });

  /** IN (peut être branché si nécessaire) */
  articleInfos = computed(() => null);
  uniteDenomination = computed(
    () =>
      ArticleInfosHelperService.uniteDenomination(this.articleInfos()) ?? "u",
  );

  startDate = input<Date>();
  endDate = input<Date>();

  /** OUT : clic bulle */
  readonly bubbleClick = output<BubbleClickPayload>();
  goOf = output();

  // === Controls ===
  viewType = signal<ViewType>("GRAPH");
  sortBy = signal<SortBy>("LOSS_EUR");
  topCount = signal<number>(10); // défaut 10
  search = signal<string>("");

  elementsView = [
    { icon: "scatter_plot", value: "GRAPH" },
    { icon: "list", value: "LIST" },
  ];
  elementsSort = [
    { name: "Tri pertes €", value: "LOSS_EUR" },
    { name: "Tri % pertes", value: "LOSS_PCT" },
    { name: "Tri conso", value: "CONSO" },
    { name: "Tri alpha", value: "ALPHA" },
  ];

  /** 🔸 Sélection courante (bulle cliquée) */
  private readonly selected = signal<{ refId: string; label: string } | null>(
    null,
  );

  /** Aplatissement */
  private readonly bilans = computed<BilanPiedDeChaine[]>(() =>
    (this.groups() ?? [])
      .flatMap((g) => g.bilans?.map((o) => o.bilan) ?? [])
      .filter((b): b is BilanPiedDeChaine => !!b),
  );

  private quantile = (arr: number[], p: number) => {
    if (!arr.length) return 0;
    const s = [...arr].sort((a, b) => a - b);
    const i = (s.length - 1) * p,
      lo = Math.floor(i),
      hi = Math.ceil(i),
      h = i - lo;
    return (1 - h) * s[lo] + h * s[hi];
  };

  /** Agrégations complètes (toutes refs) */
  private readonly rowsAll = computed<RefRow[]>(() => {
    type Agg = {
      ref: string;
      sumConso: number;
      sumTheo: number;
      sumLossPos: number;
      sumCostWeighted: number;
      n: number;
    };
    const byRef = new Map<string, Agg>();

    for (const b of this.bilans()) {
      const ref = b.of?.articleId;
      if (!ref) continue;
      const conso = parseNumber(b.consoProd) || 0;
      const theo = parseNumber(b.consoTheorique) || 0;
      const cu = parseNumber(b.coutUnitaire) || 0;

      let a = byRef.get(ref);
      if (!a) {
        a = {
          ref,
          sumConso: 0,
          sumTheo: 0,
          sumLossPos: 0,
          sumCostWeighted: 0,
          n: 0,
        };
        byRef.set(ref, a);
      }
      a.n += 1;
      a.sumConso += conso;
      a.sumTheo += theo;
      a.sumLossPos += Math.max(0, conso - theo);
      a.sumCostWeighted += cu * conso;
    }

    return Array.from(byRef.values()).map((a) => {
      const totalConso = a.sumConso;
      const unitCostWAvg = totalConso ? a.sumCostWeighted / totalConso : 0;
      const lossAbsKg = a.sumLossPos;
      const lossEur = lossAbsKg * unitCostWAvg;
      const lossPct = totalConso ? (1 - a.sumTheo / totalConso) * 100 : 0;
      return {
        ref: a.ref,
        totalConso,
        lossPct,
        lossAbsKg,
        unitCostWAvg,
        lossEur,
        n: a.n,
      };
    });
  });

  /** Recherche */
  private readonly rowsFiltered = computed(() => {
    const q = this.search().trim().toLowerCase();
    if (!q) return this.rowsAll();
    return this.rowsAll().filter((r) =>
      this.labelFor(r.ref).toLowerCase().includes(q),
    );
  });

  /** Tri */
  private readonly rowsSorted = computed(() => {
    const by = this.sortBy();
    const arr = [...this.rowsFiltered()];
    arr.sort((a, b) => {
      switch (by) {
        case "LOSS_PCT":
          return b.lossPct - a.lossPct;
        case "CONSO":
          return b.totalConso - a.totalConso;
        case "ALPHA":
          return this.labelFor(a.ref).localeCompare(this.labelFor(b.ref), "fr");
        case "LOSS_EUR":
        default:
          return b.lossEur - a.lossEur;
      }
    });
    return arr;
  });

  /** Top N */
  maxRows = computed(() => this.rowsFiltered().length);
  readonly topRows = computed<RefRow[]>(() => {
    const n = Math.max(1, Math.min(this.topCount(), this.maxRows()));
    return this.rowsSorted().slice(0, n);
  });

  /** Médianes (sur l’ensemble affiché) */
  readonly xMedian = computed(() =>
    this.quantile(
      this.topRows().map((r) => r.totalConso),
      0.5,
    ),
  );
  readonly yMedian = computed(() =>
    this.quantile(
      this.topRows().map((r) => r.lossPct),
      0.5,
    ),
  );

  /** Dataset (bulles = pertes kg) */
  readonly dataset = computed<any[]>(() => {
    const vals = this.topRows().map((r) => r.lossAbsKg);
    const min = Math.min(...vals, 0),
      max = Math.max(...vals, 1);
    const scale = (v: number) => {
      if (max === min) return 8;
      const t = (v - min) / (max - min);
      return 6 + t * 24;
    };
    return this.topRows().map((r) => ({
      x: r.totalConso,
      y: r.lossPct,
      r: scale(r.lossAbsKg),
      refId: r.ref,
      label: this.labelFor(r.ref),
      _meta: r,
    }));
  });

  /** OFs de la bulle cliquée — triés par pertes kg décroissantes */
  readonly selectedOfs = computed<OfRow[]>(() => {
    const sel = this.selected();
    if (!sel) return [];
    return this.bilans()
      .filter((b) => b.of?.articleId === sel.refId)
      .map<OfRow>((b) => {
        const conso = parseNumber(b.consoProd) || 0;
        const theo = parseNumber(b.consoTheorique) || 0;
        const pertesAbsKg = Math.max(0, conso - theo);
        const pertesPct = theo ? ((conso - theo) / theo) * 100 : 0;
        const pertesEur = pertesAbsKg * (parseNumber(b.coutUnitaire) || 0);
        return {
          of: b.of,
          consoProd: conso,
          consoTheorique: theo,
          pertesAbsKg,
          pertesPct,
          pertesEur,
        };
      })
      .sort(
        (a, b) => b.pertesAbsKg - a.pertesAbsKg || b.pertesEur - a.pertesEur,
      );
  });

  /** Formatters */
  fmtPct = (v: number) =>
    `${(Math.round(v * 10) / 10).toLocaleString("fr-FR")} %`;
  nf = new Intl.NumberFormat("fr-FR");

  /** Canvas + Chart */
  canvasRef = viewChild<ElementRef<HTMLCanvasElement>>("quickWin");
  private chart?: Chart;

  constructor() {
    // Reset du slider à 10 à chaque proc des stats + clamp initial
    effect(
      () => {
        this.stats(); // dépendance
        const RESET = 10;
        const max = this.maxRows();
        const clamped = Math.min(Math.max(1, RESET), Math.max(1, max));
        this.topCount.set(clamped);
      },
      { allowSignalWrites: true },
    );

    // Création/Rebuild du chart quand le canvas est prêt
    effect(
      () => {
        if (!this.canvasRef()) return;
        if (!this.chart) this.buildChart();
      },
      { allowSignalWrites: true },
    );

    // MAJ datasets + titres + redraw
    effect(() => this.updateChart(), { allowSignalWrites: true });

    // Reset sélection quand la période change
    effect(
      () => {
        this.startDate();
        this.endDate();
        this.selected.set(null);
      },
      { allowSignalWrites: true },
    );

    // Clamp TopN si le max varie
    effect(
      () => {
        const max = this.maxRows();
        const cur = this.topCount();
        const clamped = Math.min(Math.max(1, cur), Math.max(1, max));
        if (clamped !== cur) this.topCount.set(clamped);
      },
      { allowSignalWrites: true },
    );
  }

  private buildChart() {
    const canvas = this.canvasRef();
    const ctx = canvas?.nativeElement.getContext("2d");
    if (!ctx) return;

    // === Couleurs depuis le thème (CSS variables) ===
    const css = getComputedStyle(canvas!.nativeElement);
    const ON_BG = (
      css.getPropertyValue("--color-on-background") || "#1d252c"
    ).trim();
    const ON_BG_ALT = (
      css.getPropertyValue("--color-on-background-alt") || "#476373"
    ).trim();
    const OUTLINE = (
      css.getPropertyValue("--color-outline") || "#9db8c3"
    ).trim();
    const ACCENT = (css.getPropertyValue("--color-accent") || "#c535ff").trim();
    const ON_ACCENT_ALT = (
      css.getPropertyValue("--color-on-accent-alt") || "rgba(197,53,255,.5)"
    ).trim();
    const WARNING_TXT = (
      css.getPropertyValue("--color-on-warning") || "#f96900"
    ).trim();
    const WARNING_BG60 = (
      css.getPropertyValue("--color-warning-60") || "rgba(255,240,211,.6)"
    ).trim();

    const withAlpha = (color: string, aHex = "80") =>
      color.startsWith("#") && color.length === 7 ? `${color}${aHex}` : color;

    const OUTLINE_50 = withAlpha(OUTLINE, "80"); // ~50% alpha
    const GRID_COLOR = OUTLINE_50;
    const TICKS_COLOR = ON_BG_ALT;

    // Couleurs du scatter (alignées sur "CA par période")
    const BUBBLE_FILL = ON_ACCENT_ALT;
    const BUBBLE_BORDER = ACCENT;

    // ——— Typo
    const FONT_FAMILY =
      css.getPropertyValue("--font-family")?.trim() ||
      "Sora, system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif";
    const FONT_SIZE_BODY = 12;

    // === Plugin: zone Quick Win derrière les points + repères au-dessus
    const quadrantPlugin = {
      id: "quadrants",
      // Derrière les datasets (remplissage)
      beforeDatasetsDraw: (chart: Chart) => {
        const { ctx, chartArea, scales } = chart as any;
        if (!chartArea) return;
        const xm = (scales.x as any).getPixelForValue(this.xMedian());
        const ym = (scales.y as any).getPixelForValue(this.yMedian());

        ctx.save();
        ctx.fillStyle = WARNING_BG60; // zone remplie (derrière)
        ctx.fillRect(
          xm,
          chartArea.top,
          chartArea.right - xm,
          ym - chartArea.top,
        );
        ctx.restore();
      },
      // Devant: libellé + lignes pointillées
      afterDatasetsDraw: (chart: Chart) => {
        const { ctx, chartArea, scales } = chart as any;
        if (!chartArea) return;
        const xm = (scales.x as any).getPixelForValue(this.xMedian());
        const ym = (scales.y as any).getPixelForValue(this.yMedian());

        ctx.save();
        // Légende
        ctx.fillStyle = WARNING_TXT;
        ctx.font = `600 ${FONT_SIZE_BODY}px ${FONT_FAMILY}`;
        ctx.fillText("Zone Quick Wins", xm + 10, chartArea.top + 18);

        // Lignes médianes (pointillées)
        ctx.strokeStyle = OUTLINE_50;
        ctx.setLineDash([4, 4]);
        ctx.beginPath();
        ctx.moveTo(xm, chartArea.top);
        ctx.lineTo(xm, chartArea.bottom);
        ctx.stroke();
        ctx.beginPath();
        ctx.moveTo(chartArea.left, ym);
        ctx.lineTo(chartArea.right, ym);
        ctx.stroke();
        ctx.restore();
      },
    };

    // (Re)création du chart
    this.chart?.destroy();
    this.chart = new Chart(ctx, {
      type: "bubble",
      data: {
        datasets: [
          {
            label: "Références (Top pertes €)",
            data: this.dataset() as any, // {x,y,r,refId,label,_meta}
            backgroundColor: BUBBLE_FILL,
            borderColor: BUBBLE_BORDER,
            borderWidth: 1.5,
            radius: (c: any) => c.raw?.r ?? 6,
            hoverRadius: (c: any) => (c.raw?.r ?? 8) + 2,
            hitRadius: (c: any) => Math.max(8, c.raw?.r ?? 10),
          },
        ],
      },
      options: {
        plugins: {
          legend: { display: false },
          tooltip: {
            callbacks: {
              label: (ctx) => {
                const d: any = ctx.raw;
                const m: RefRow = d._meta;
                return [
                  `${d.label}`,
                  `Σ conso: ${Math.round(m.totalConso).toLocaleString("fr-FR")} ${this.uniteDenomination()}`,
                  `% pertes moy.: ${this.fmtPct(m.lossPct)}`,
                  `Pertes: ${Math.round(m.lossAbsKg).toLocaleString("fr-FR")} ${this.uniteDenomination()}`,
                  `Pertes €: ${Math.round(m.lossEur).toLocaleString("fr-FR")} €`,
                ];
              },
            },
          },
        },
        responsive: true,
        maintainAspectRatio: false,
        resizeDelay: 100,
        layout: { padding: { left: 4, right: 10, top: 6, bottom: 6 } },
        onHover: (_evt, active) => {
          const el = this.canvasRef()?.nativeElement;
          if (el) el.style.cursor = active?.length ? "pointer" : "default";
        },
        onClick: (_evt, elements, chart) => {
          const el = (elements as any)?.[0];
          if (!el) return;
          const raw: any = chart.data.datasets[el.datasetIndex].data[el.index];
          const m: RefRow = raw._meta;
          this.selected.set({ refId: raw.refId, label: raw.label });
          this.bubbleClick.emit({
            refId: raw.refId,
            label: raw.label,
            avgConso: m.totalConso,
            avgLossPct: m.lossPct,
            lossEur: m.lossEur,
          });
        },
        scales: {
          x: {
            title: {
              display: true,
              text: `Impact (quantité totale consommée en ${this.uniteDenomination()})`,
              color: ON_BG,
              font: { family: FONT_FAMILY, size: 12, weight: "lighter" },
            },
            grid: { color: GRID_COLOR },
            ticks: {
              color: TICKS_COLOR,
              font: { family: FONT_FAMILY, size: 12 },
            },
            border: { color: OUTLINE },
          },
          y: {
            title: {
              display: true,
              text: "% de perte moyen",
              color: ON_BG,
              font: { family: FONT_FAMILY, size: 12, weight: "lighter" },
            },
            grid: { color: GRID_COLOR },
            ticks: {
              color: TICKS_COLOR,
              font: { family: FONT_FAMILY, size: 12 },
            },
            border: { color: OUTLINE },
          },
        },
        elements: { point: { hoverRadius: 4, hitRadius: 8 } },
      },
      plugins: [quadrantPlugin as any],
    });
  }

  /** Update */
  private updateChart() {
    if (!this.chart || this.viewType() !== "GRAPH") return;

    // datasets
    this.chart.data.datasets[0].data = this.dataset() as any;

    this.chart.update("none");
  }

  /** Helpers */
  clearSelection() {
    this.selected.set(null);
  }

  selectedLabel() {
    return this.selected()?.label ?? "";
  }

  labelFor(refId: string) {
    return (
      this.articleInfosFacade.getByArticleId(refId)?.()?.denomination ?? refId
    );
  }

  protected readonly TypePercent = TypePercent;
  protected readonly Math = Math;
}
